<template>
  <section class="map-area">
    <div id="map_canvas"></div>

    <transition name="fade">
      <div class="reset-filter" v-if="isFilterEnabled">
        <button class="uk-button uk-button-small" @click="onResetFilterClicked" v-wave>
          <span uk-icon="icon: close; ratio: 0.7"></span> {{ messages.map_reset_filters }}
        </button>
      </div>
    </transition>

    <slot name="panel-opener">
      <div class="panel-opener" :class="{open: !isPanelClosed}" @click="onOpenPanelClicked">
        <span>{{ messages.map_open }}</span>
        <span uk-icon="icon: search; ratio: 1"></span>
      </div>
    </slot>

    <div class="guide-wrapper to-panel" @click="onOpenPanelClicked" v-if="guide.show" v-wave>
      <span class="guide">
        <i class="material-symbols-sharp" data-icon="arrow_back"></i>
        {{ messages.map_guide_to_panel }}
      </span>
    </div>

    <div class="guide-wrapper to-location" @click="onLocationGuideClicked" v-if="guide.show" v-wave>
      <span class="guide">
        {{ messages.map_guide_to_location }}
        <i class="material-symbols-sharp" data-icon="arrow_forward"></i>
      </span>
    </div>

    <!-- 現在地取得は操作を止めないよう、別の方法で表現する -->
    <Loading
      :show="mapReady > 0"
      :message="MapService.locationManager?.isLoading ? messages.common_getting_current_location : messages.common_loading"
      :className="isSelectedPoints ? 'selected' : ''">
    </Loading>

    <a class="sl-powered-by" copywrite href="https://www.goga.co.jp/services/storelocator/" target="_blank">Powered<br>by GOGA</a>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import uikit from 'uikit'

import store from '@/store'

import Loading from '@/components/common/Loading.vue'

import MapService from '@/services/MapService.ts'
import PointService from '@/services/PointService.ts'
import CommonService from '@/services/CommonService.ts'

import { Filters } from '@/services/Filters.ts'
import { Panel } from '@/constants'

CommonService.onBeforePanToCurrentLocation = () => {
  guide.value.show = false
}

/**
 * 位置情報へのアクセスが無効：取得できない旨出す＋地名検索を促す
 * 位置情報へのアクセスが有効＋現在地の取得に失敗：取得できない旨出す
 */
CommonService.onFailedPanToCurrentLocation = (e) => {
  // 誘導あり
  let message = messages.value.map_failed_to_get_current_location_to_places

  // 拒否は明示的にわかるように
  if (e.code === MapService.locationManager.CURRENT_LOCATION_RESULT_DENIED) {
    message = messages.value.map_denied_to_get_current_location_to_places
  } else {
    console.warn(e)
  }

  uikit.notification.closeAll()
  uikit.notification(
    message,
    {pos: "top-center", timeout: 5000}
  )

  focusAutocomplete()
}

/**
 * 地名検索にフォーカス
 * パネルも開く
 * 
 * フォーカスは現在地取得済ならスキップ
 */
function focusAutocomplete() {
  // console.debug('focusAutocomplete', MapService.locationManager)

  // パネル開き、地名検索に誘導
  store.dispatch("changePanel", {value: Panel.States.OPENED})

  if (MapService.locationManager.location) {
    return
  }

  const autocomplete = document.getElementById('autocomplete')
  if (autocomplete) {
    autocomplete.focus()

    // ここでメッセージを出してもいいが、現在地の取得失敗と被るので、一旦なし
  }
}

/**
 * 絞り込みをリセット
 * 
 * TODO
 * ここで持つものではない印象がある、ただマップの初期化より後に起動したいのか
 * 処理はなるべくFiltersに移し、呼び出しだけ行いたい
 * 懸念はvuexに依存していること、これは外から仕込む？
 * 現状、選択したフィルタはvuex側に補完しているので、Filtersにもっても意味が薄いのはある
 */
function resetFilter() {
  // console.debug('resetFilter')

  store.dispatch("resetFilter")

  const patterns = [
    {
      type: Filters.TYPE_MARKERS,
      initialFilters: Filters.getInitialFilterIds(Filters.TYPE_MARKERS),
      selectedFiltersInStore: store.state.filters.selectedMarkers,
      selectedFiltersInConfig: storelocator.selected_markers
    },
    {
      type: Filters.TYPE_SEARCH_CONDITIONS,
      initialFilters: Filters.getInitialFilterIds(Filters.TYPE_SEARCH_CONDITIONS),
      selectedFiltersInStore: store.state.filters.selectedSearchConditions,
      selectedFiltersInConfig: storelocator.selected_search_conditions
    }
  ]

  // console.debug('resetFilter', patterns)

  patterns.forEach(pattern => {
    pattern.initialFilters.forEach(id => {
      store.dispatch("changeFilter", {
        type: pattern.type,
        id: id
      })
    })
  })

  // 親を選択、同階層（指定したフィルタと同じ親に所属するもの）を全て選択してたら
  patterns.forEach(pattern => {
    pattern.initialFilters.forEach(id => {
      // 親を取得
      const parent = Filters.findParentById(pattern.type, id)
      if (!parent) {
        return
      }

      // 同階層の選択中を取得
      const siblingIds = parent.children.map(p => p.id)
      const selectedSiblingIds = siblingIds.map(sid => {
        return pattern.selectedFiltersInStore.includes(sid)
      })

      // 親を選択してない＋同階層を全て選択中：親を選択
      if (!pattern.selectedFiltersInStore.includes(parent.id) && !selectedSiblingIds.includes(false)) {
        pattern.selectedFiltersInConfig.push(parent.id)
        store.dispatch("changeFilter", {
          type: pattern.type,
          id: parent.id,
          parent: true
        })
      }
    })
  })

  Filters.cache.updateLocation()

  // ここで呼ばないとURLで指定した絞り込みがキャッシュに乗らない（店舗の一覧に引き継げない）
  Filters.cache.save(Filters.selectedFilters)

  // ここで呼ばないと最寄り店へのガイドが最新化されない
  CommonService.onIdle()
}

/**
 * ガイドまわり
 */
const guide = ref({
  show: false
})

onMounted(async () => {
  await MapService.init({
    locationManager: {
      onClicked: async () => {
        await CommonService.panToCurrentLocation()
      }
    },
    pointManager: {
      // マップは基本的にクラスタ有効化
      gridSize: storelocator.map?.markers?.clusters?.enabled
        ? storelocator.map?.markers?.clusters?.grid?.size || 50
        : 0,
      onClicked: CommonService.onClusterClicked,
      onInnerMarkerClicked: CommonService.onInnerMarkerClicked,
      isShowPoint: PointService.checkPoint
    }
  })

  MapService.onClicked = () => {
    guide.value.show = false
  }

  // クラスタ・Point詳細
  PointService.initPointWindow({
    formats: storelocator.map?.point_window?.formats,
    searchConditions: storelocator.map?.point_window?.search_conditions,
    onInnerPointSelected: CommonService.onInnerPointSelected,
    onClosed: CommonService.onPointClosed,
    onBacked: CommonService.onPointBacked
  })

  // イベント
  google.maps.event.addListener(MapService.map, 'idle', async () => {
    CommonService.onIdle()
  })

  resetFilter()
  store.dispatch('onFilterInitialized')

  // キャッシュなしなら現在地・地名検索に誘導、もしくは案内だし
  if (!MapService.locationCache.loadedCache) {
    if (storelocator.map?.location?.default?.pan_to_current_location) {
      await CommonService.panToCurrentLocation()
    } else {
      // 一定時間で消す
      guide.value.show = true
      setTimeout(() => {
        guide.value.show = false
      }, 3750)

      // 意外とわかりにくいので一旦やめておく、今後検討
      // focusAutocomplete()
    }
  }
})

/**
 * フィルタリセットをクリック時
 */
function onResetFilterClicked() {
  resetFilter()

  storelocator.analytics.client?.send('ResetFilter', {
  })
}

function onOpenPanelClicked() {
  guide.value.show = false

  store.dispatch("changePanel", {
    value: Panel.States.OPENED
  })

  // 自動フォーカスするか考える、過剰にかかっても微妙なので、様子みながら
  focusAutocomplete()

  storelocator.analytics.client?.send('ToggleMapPanel', {
    MapPanelAction: 'open'
  })
}

function onLocationGuideClicked() {
  CommonService.panToCurrentLocation()
}

const mapReady = computed(() => store.state.mapReady)
const isFilterEnabled = computed(() => store.getters.isFilterEnabled)
const isPanelClosed = computed(() => store.getters.isPanelClosed)
const messages = computed(() => storelocator.messages)

/**
 * Point・クラスタいずれか選択中
 */
const isSelectedPoints = computed(() => store.getters.isPointSelected || store.getters.isClusterSelected)

</script>

<style lang="scss">
#map_canvas {
  width: 100%;
  height: 100%;
}

.panel-opener {
  position: absolute;
  bottom: auto;
  left: 0px;
  background-color: var(--theme-color);
  cursor: pointer;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--text-color);
  box-shadow: 0 1px 4px rgb(0 0 0 / 50%);
  top: 16px;
  border-radius: 0 1.5rem 1.5rem 0;
  padding: 0 .75rem 0 .5rem;

  .uk-icon {
    margin-left: .5rem
  }

  @media only screen and (min-width: $breakpoint-medium) {
    display: none
  }

  &:hover {
    transition: all .25s;
    .uk-icon {
      margin-left: .75rem;
      transition: all .25s;
    }
  }
}
.sl-powered-by {
  position: absolute;
  display: block;
  bottom: 2rem;
  border-radius: 3px;
  left: .7rem;
  margin: 0;
  font-size: 9px;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 4px 6px;
  color: #333;
  border: solid 1px #888;
}

.reset-filter {
  box-sizing: border-box;
  opacity: .9;

  button {
    background-color: #fff;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1em;
    height: 32px;
    box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
    display: flex;
    align-items: center;
    padding: 0 1rem;
    transition: all 0.3s;
    &:hover {
      background-color: var(--theme-color);
      color: var(--text-color);
    }
    .uk-icon {
      flex-shrink: 0;
      padding-right: 0.5rem;
      transform: scale(1.5);
    }
  }

  @media screen and (min-width: $breakpoint-small) {
    top: 1.5rem;
    bottom: auto;
  }
}

.map-area {
  position: relative;
  width: 100%;
  height: 100%;

  .reset-filter {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    @media screen and (max-width: $breakpoint-small) {
      bottom: 2rem;
      // 上にもってくる場合はこれを指定、ただ副作用もあるので一旦なし
      // z-index: 1000;
    }
  }
}

@keyframes fade-in-guide {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.guide-wrapper {
  animation: fade-in-guide 1.2s ease-in-out;
  background-color: rgba(0, 0, 0, .75);
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  margin-bottom: .5rem;
  padding: .5rem;
  width: auto;
  
  .guide {
    position: relative;

    i {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  @keyframes fade-out-guide {
    0% {
      opacity: 0;
    }
    10% {
      opacity: 0;
    }
    20% {
      opacity: 1;
    }
    80% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  &.to-panel {
    animation: fade-out-guide 4s ease-in-out;
    position: absolute;
    top: 1.25rem;
    left: 6rem;
    .guide {
      padding: .5rem .5rem .5rem 2rem;
    }
    i {
      left: 0.1rem;
    }
    @media screen and (min-width: $breakpoint-small) {
      left: 1.25rem;
    }
  }

  &.to-location {
    animation: fade-out-guide 4s ease-in-out;
    position: absolute;
    bottom: 7.5rem;
    right: 4rem;
    .guide {
      padding: .5rem 2rem .5rem .5rem;
    }
    i {
      right: 0.1rem;
    }
  }

  &:hover {
    background-color: rgba(0, 0, 0, .7);
  }
}


.gm-bundled-control {
  .gm-svpc {
    margin-top: .6rem !important;
  }
}
</style>
